/**
 * admin/quiz-submissions/$id.tsx — Admin review a tutor-submitted quiz (Phase F.3).
 *
 * Loads GET /api/quiz/admin/:id (same handler used by the edit page).
 * Renders the quiz + questions in read-only mode via QuestionEditor.
 * Action bar at the bottom:
 *   - "Setujui" → POST /api/quiz/admin/:id/approve
 *   - "Tolak"   → opens a modal with a reason textarea → POST /api/quiz/admin/:id/reject
 *
 * Read-only mode is critical: rejecting/approving locks the source,
 * and the tutor gets the result via reject_reason on their own list.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { createFileRoute, redirect, useNavigate, useParams } from '@tanstack/react-router';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import {
  QuestionEditor,
  type QuestionDraft,
} from '../../../components/admin/QuestionEditor.js';
import { freshSessionQuery } from '../../../lib/api/queries.js';

export const Route = createFileRoute('/admin/quiz-submissions/$id')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
    return null;
  },
  component: AdminSubmissionReviewPage,
});

interface ApiQuiz {
  id: string;
  title: string;
  description: string | null;
  source: 'library' | 'custom' | 'library_pending';
  sd_grade: number;
  questions: Array<{
    id: string;
    type: 'single_choice' | 'true_false' | 'multi_select';
    difficulty: 'easy' | 'medium' | 'hard';
    prompt: string;
    options_json: string | null;
    correct_answer_json: string;
    points: number;
  }>;
}

function draftFromApi(q: ApiQuiz['questions'][number]): QuestionDraft {
  return {
    type: q.type,
    difficulty: q.difficulty,
    prompt: q.prompt,
    options: q.options_json ? (JSON.parse(q.options_json) as string[]) : null,
    correctAnswer: q.correct_answer_json,
  };
}

function AdminSubmissionReviewPage(): ReactNode {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [quiz, setQuiz] = useState<ApiQuiz | null>(null);
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/quiz/admin/${encodeURIComponent(id)}`, { credentials: 'include' });
        const j = (await r.json()) as { ok: boolean; data?: ApiQuiz; error?: { message: string } };
        if (cancelled) return;
        if (!j.ok || !j.data) {
          setLoadError(j.error?.message ?? 'Gagal memuat submission.');
          setLoading(false);
          return;
        }
        setQuiz(j.data);
        setQuestions(j.data.questions.map(draftFromApi));
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError((err as Error).message);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const approve = async (): Promise<void> => {
    setActionError(null);
    setBusy(true);
    try {
      const r = await fetch(`/api/quiz/admin/${encodeURIComponent(id)}/approve`, {
        method: 'POST',
        credentials: 'include',
      });
      const j = (await r.json()) as { ok: boolean; error?: { message: string } };
      if (!j.ok) {
        setActionError(j.error?.message ?? 'Gagal menyetujui.');
        setBusy(false);
        return;
      }
      await navigate({ to: '/admin/quizzes' });
    } catch (err) {
      setActionError((err as Error).message);
      setBusy(false);
    }
  };

  const reject = async (): Promise<void> => {
    setActionError(null);
    setBusy(true);
    try {
      const r = await fetch(`/api/quiz/admin/${encodeURIComponent(id)}/reject`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason.trim() }),
      });
      const j = (await r.json()) as { ok: boolean; error?: { message: string } };
      if (!j.ok) {
        setActionError(j.error?.message ?? 'Gagal menolak.');
        setBusy(false);
        return;
      }
      setShowRejectModal(false);
      await navigate({ to: '/admin/quizzes' });
    } catch (err) {
      setActionError((err as Error).message);
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Review Submission" title="Memuat…" />
      </main>
    );
  }
  if (loadError || !quiz) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Review Submission" title="Submission tidak ditemukan" />
        <StatePanel variant="error" title="Gagal memuat" description={loadError ?? 'Tidak ditemukan'} />
      </main>
    );
  }

  return (
    <main className="page-shell">
      <PageHeader
        eyebrow="Review Submission"
        title={quiz.title}
        description={`Kelas ${quiz.sd_grade} · ${quiz.questions.length} soal`}
      />

      {quiz.source !== 'library_pending' ? (
        <div className="form-message form-message-muted" data-testid="already-reviewed">
          Submission ini sudah diproses (status saat ini: {quiz.source}). Tombol di bawah tidak akan berpengaruh.
        </div>
      ) : null}

      {actionError ? (
        <div className="form-message is-error" role="alert" data-testid="action-error">
          {actionError}
        </div>
      ) : null}

      {quiz.description ? <p>{quiz.description}</p> : null}

      <section className="section">
        <h2 className="section-title">Soal (read-only)</h2>
        <div className="stack">
          {questions.map((q, i) => (
            <QuestionEditor key={i} index={i} value={q} readOnly />
          ))}
        </div>
      </section>

      <div className="cluster" style={{ marginTop: 'var(--spacing-4)' }}>
        <button
          type="button"
          className="button button-primary"
          onClick={approve}
          disabled={busy || quiz.source !== 'library_pending'}
          data-testid="submission-approve"
        >
          Setujui
        </button>
        <button
          type="button"
          className="button button-secondary"
          onClick={() => setShowRejectModal(true)}
          disabled={busy || quiz.source !== 'library_pending'}
          data-testid="submission-reject"
        >
          Tolak
        </button>
      </div>

      {showRejectModal ? (
        <div className="modal-backdrop" role="presentation" onClick={() => setShowRejectModal(false)}>
          <div
            className="modal-card modal-card-narrow"
            role="document"
            onClick={(e) => e.stopPropagation()}
            data-testid="reject-modal"
          >
            <div className="modal-header">
              <h2 className="modal-title">Tolak submission</h2>
              <button
                type="button"
                className="modal-close"
                aria-label="Tutup"
                onClick={() => setShowRejectModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <label className="form-field">
                <span className="form-label">Alasan penolakan (wajib)</span>
                <textarea
                  rows={4}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Contoh: Soal 3 tidak sesuai tingkat kelas 2."
                  data-testid="reject-reason"
                />
              </label>
              <div className="cluster" style={{ marginTop: 'var(--spacing-3)' }}>
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => setShowRejectModal(false)}
                  disabled={busy}
                >
                  Batal
                </button>
                <button
                  type="button"
                  className="button button-primary"
                  onClick={reject}
                  disabled={busy || !rejectReason.trim()}
                  data-testid="reject-confirm"
                >
                  {busy ? 'Mengirim…' : 'Tolak'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
